import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Landmark, HandCoins, AlertCircle, RefreshCcw, Link2, PlugZap, ArrowRight,
  Wallet, TrendingUp, PiggyBank,
} from "lucide-react";
import { toast } from "sonner";
import Giving from "@/pages/Giving";

const BRAND = {
  terracotta: "#B2674A",
  sage: "#AEBDB1",
  gold: "#C69E5F",
  primary: "#425252",
  peach: "#DCA591",
};

const currency = (n, cents = false) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return `$${v.toLocaleString(undefined, {
    maximumFractionDigits: cents ? 2 : 0,
    minimumFractionDigits: cents ? 2 : 0,
  })}`;
};

function TabButton({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-2.5 text-sm font-medium rounded-full transition-colors ${
        active
          ? "bg-terracotta text-white shadow-warm"
          : "text-brand-primary/70 hover:text-brand-primary"
      }`}
    >
      {children}
    </button>
  );
}

function MinistryCard({ ministry }) {
  const {
    key, label, class_name, kind, subtitle, has_income,
    mtd_income, ytd_income, mtd_expense, ytd_expense, ytd_net,
    annual_budget, remaining_budget, pct_used, budget_source,
    annual_budget_income, annual_budget_expense, expense_over_under, income_vs_budget,
  } = ministry;
  const showIncomeBudget  = has_income !== false && annual_budget_income  != null;
  const showExpenseBudget = annual_budget_expense != null;
  const hasBudget = annual_budget != null || showIncomeBudget || showExpenseBudget;
  const budgetValue = annual_budget;
  // Departments without income accounts hide the income grid entirely
  // (operator §16: do not display fake $0 income fields).
  const showIncome = has_income !== false && (mtd_income != null || ytd_income != null);
  const showNet = has_income !== false && ytd_net != null;
  const pctColor =
    pct_used == null ? "text-brand-primary/40"
    : pct_used > 100 ? "text-terracotta"
    : pct_used > 85 ? "text-gold"
    : "text-sage";
  const barColor =
    pct_used == null ? "bg-brand-primary/10"
    : pct_used > 100 ? "bg-terracotta"
    : pct_used > 85 ? "bg-gold"
    : "bg-sage";
  const netColor = (ytd_net ?? 0) >= 0 ? "text-sage" : "text-terracotta";
  // Rollup / class / department heading labels.
  const chipLabel =
    kind === "total" ? "Organization" :
    kind === "class" ? "QBO Class" : "Ministry";
  return (
    <Card
      data-testid={`finance-ministry-${key}`}
      className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-terracotta font-semibold">{chipLabel}</p>
          <h3 className="font-heading text-xl text-brand-primary mt-0.5" data-testid={`finance-ministry-${key}-label`}>
            {label}
          </h3>
          {subtitle && (
            <p className="text-[11px] text-brand-primary/50 mt-0.5">{subtitle}</p>
          )}
          {!subtitle && class_name && (
            <p className="text-[11px] text-brand-primary/50 mt-0.5 truncate" title={`QuickBooks Class: ${class_name}`}>
              QBO class · {class_name}
            </p>
          )}
        </div>
      </div>

      {/* Income / Expense grid — income cells hidden for expense-only depts */}
      <div className={`grid ${showIncome ? "grid-cols-2" : "grid-cols-2"} gap-3`}>
        {showIncome && (
          <>
            <div className="rounded-xl bg-sage/10 border border-sage/25 p-3">
              <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">MTD Income</p>
              <p data-testid={`finance-ministry-${key}-mtd-income`} className="font-heading text-lg font-semibold text-brand-primary mt-0.5">
                {currency(mtd_income)}
              </p>
            </div>
            <div className="rounded-xl bg-sage/15 border border-sage/30 p-3">
              <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">YTD Income</p>
              <p data-testid={`finance-ministry-${key}-ytd-income`} className="font-heading text-lg font-semibold text-brand-primary mt-0.5">
                {currency(ytd_income)}
              </p>
            </div>
          </>
        )}
        <div className="rounded-xl bg-peach/25 border border-terracotta/15 p-3">
          <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">MTD Expense</p>
          <p data-testid={`finance-ministry-${key}-mtd-expense`} className="font-heading text-lg font-semibold text-brand-primary mt-0.5">
            {currency(mtd_expense)}
          </p>
        </div>
        <div className="rounded-xl bg-peach/30 border border-terracotta/20 p-3">
          <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">YTD Expense</p>
          <p data-testid={`finance-ministry-${key}-ytd-expense`} className="font-heading text-lg font-semibold text-brand-primary mt-0.5">
            {currency(ytd_expense)}
          </p>
        </div>
      </div>

      {/* YTD Net — hidden for expense-only departments */}
      {showNet && (
        <div className="flex items-center justify-between border-t border-brand-primary/10 pt-3">
          <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">YTD Net</p>
          <p data-testid={`finance-ministry-${key}-ytd-net`} className={`font-heading text-base font-semibold ${netColor}`}>
            {currency(ytd_net)}
          </p>
        </div>
      )}

      {/* Budget block — Annual Income + Annual Expense budgets pulled
          from QuickBooks (full Jan → Dec), each with a variance line
          against YTD actuals. */}
      <div className="border-t border-brand-primary/10 pt-4">
        {hasBudget ? (
          <div className="flex flex-col gap-4">
            {/* Header + progress bar (expense-side, matches pct_used). */}
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">
                Annual Budget
                {budget_source === "admin_override" && (
                  <span className="ml-1.5 text-[9px] normal-case tracking-normal text-brand-primary/40">(admin-entered)</span>
                )}
              </p>
              <p
                data-testid={`finance-ministry-${key}-pct-used`}
                className={`font-heading text-sm font-semibold ${pctColor}`}
              >
                {pct_used == null ? "—" : `${pct_used.toFixed(1)}% of expense budget`}
              </p>
            </div>
            <div className="h-2 w-full rounded-full bg-brand-primary/5 overflow-hidden -mt-2">
              <div
                className={`h-full ${barColor} transition-all duration-500`}
                style={{ width: `${Math.min(100, Math.max(0, pct_used || 0))}%` }}
              />
            </div>

            {/* Income budget row (hidden for expense-only depts). */}
            {showIncomeBudget && (
              <div className="rounded-xl border border-sage/25 bg-sage/5 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">Annual Income Budget</p>
                  <p
                    data-testid={`finance-ministry-${key}-budget-income`}
                    className="font-heading text-base font-semibold text-brand-primary"
                  >
                    {currency(annual_budget_income)}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[11px] text-brand-primary/60">Income vs Annual Budget</p>
                  <p
                    data-testid={`finance-ministry-${key}-income-vs-budget`}
                    className={`text-sm font-semibold ${(income_vs_budget ?? 0) >= 0 ? "text-sage" : "text-terracotta"}`}
                    title="YTD Actual Income minus Annual Income Budget. Positive = ahead of plan."
                  >
                    {income_vs_budget == null ? "—" : `${income_vs_budget >= 0 ? "+" : ""}${currency(income_vs_budget)}`}
                  </p>
                </div>
              </div>
            )}

            {/* Expense budget row + over/under. */}
            {showExpenseBudget && (
              <div className="rounded-xl border border-terracotta/20 bg-peach/20 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">Annual Expense Budget</p>
                  <p
                    data-testid={`finance-ministry-${key}-budget-expense`}
                    className="font-heading text-base font-semibold text-brand-primary"
                  >
                    {currency(annual_budget_expense)}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[11px] text-brand-primary/60">
                    {(expense_over_under ?? 0) >= 0 ? "Under Budget" : "Over Budget"}
                  </p>
                  <p
                    data-testid={`finance-ministry-${key}-expense-over-under`}
                    className={`text-sm font-semibold ${(expense_over_under ?? 0) >= 0 ? "text-sage" : "text-terracotta"}`}
                    title="Annual Expense Budget minus YTD Actual Expense. Positive = under budget."
                  >
                    {expense_over_under == null ? "—" : `${expense_over_under >= 0 ? "+" : ""}${currency(expense_over_under)}`}
                  </p>
                </div>
              </div>
            )}

            {/* Legacy combined-remaining line — only shown when we have
                nothing more granular (e.g. admin_override). */}
            {!showIncomeBudget && !showExpenseBudget && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">Annual Budget</p>
                  <p
                    data-testid={`finance-ministry-${key}-budget`}
                    className="font-heading text-base font-semibold text-brand-primary"
                  >
                    {currency(budgetValue)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">Remaining</p>
                  <p
                    data-testid={`finance-ministry-${key}-remaining`}
                    className="text-sm font-semibold text-brand-primary"
                  >
                    {currency(remaining_budget)}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            data-testid={`finance-ministry-${key}-no-budget`}
            className="text-xs text-brand-primary/50 italic flex items-center gap-2"
          >
            <PiggyBank className="h-4 w-4" strokeWidth={1.6} />
            No annual budget from QuickBooks. Add a QBO Budget for this class (or an admin override in the mapping editor).
          </div>
        )}
      </div>
    </Card>
  );
}

function OverallTile({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-4 flex items-center gap-3">
      <div
        className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
        style={{ background: `${color}1A` }}
      >
        <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.7} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-brand-primary/60">{label}</p>
        <p data-testid={testid} className="font-heading text-lg font-semibold text-brand-primary mt-0.5">
          {value}
        </p>
      </div>
    </div>
  );
}

function OverallTotalsBar({ companyTotals }) {
  const t = companyTotals || {};
  const net_ytd = t.ytd_net ?? 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <OverallTile testid="finance-total-mtd-income"  label="MTD Income"  value={currency(t.mtd_income)}  color={BRAND.sage}       icon={TrendingUp} />
      <OverallTile testid="finance-total-ytd-income"  label="YTD Income"  value={currency(t.ytd_income)}  color={BRAND.sage}       icon={HandCoins} />
      <OverallTile testid="finance-total-mtd-expense" label="MTD Expense" value={currency(t.mtd_expense)} color={BRAND.terracotta} icon={Wallet} />
      <OverallTile testid="finance-total-ytd-expense" label="YTD Expense" value={currency(t.ytd_expense)} color={BRAND.terracotta} icon={Wallet} />
      <OverallTile
        testid="finance-total-net-ytd"
        label="Net YTD"
        value={currency(net_ytd)}
        color={net_ytd >= 0 ? BRAND.sage : BRAND.terracotta}
        icon={net_ytd >= 0 ? TrendingUp : Wallet}
      />
    </div>
  );
}

function MinistryFinanceTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const load = async (force = false) => {
    setRefreshing(true);
    setSummaryError(null);
    // STEP 1 — always fetch status first. This is the gate.
    let st;
    try {
      const r = await api.get("/oauth/quickbooks/status");
      st = r.data || { connected: false };
    } catch {
      st = { connected: false };
    }
    setStatus(st);

    // STEP 2 — ONLY when connected do we request Finance data. When
    // disconnected we render the reconnect banner and issue ZERO
    // /finance/summary, /finance/classes, or /finance/mapping requests.
    if (st?.connected) {
      try {
        const r = await api.get(`/finance/summary${force ? "?refresh=1" : ""}`);
        setData(r.data);
      } catch (e) {
        // Finance-data error must NEVER break the QuickBooks connection
        // itself. Preserve status; surface a scoped error on the page.
        const detail = e?.response?.data?.detail || e?.message || "Failed to load Finance data";
        setSummaryError({ detail, status: e?.response?.status || 0 });
      }
    } else {
      // Clear any stale summary payload from a previous session so the
      // disconnected state renders cleanly.
      setData(null);
    }
    setRefreshing(false);
    setLoading(false);
  };

  // StrictMode-safe initial load — guards against dev-mode double invoke.
  const _initialLoadRef = useRef(false);
  useEffect(() => {
    if (_initialLoadRef.current) return;
    _initialLoadRef.current = true;
    load();
  }, []);

  // Surface QBO callback status from the URL (?qbo=connected | qbo_error=…)
  //
  // Success is NOT presumed from the query param alone — the callback URL
  // could be stale (bookmark, back button, half-completed OAuth). We
  // re-verify by calling /api/oauth/quickbooks/status and only surface
  // "QuickBooks connected" if the server confirms `connected: true`.
  //
  // A ref guards against React.StrictMode's double-invoke of effects in
  // dev mode so we don't fire duplicate toasts / status requests.
  const _handledCallbackRef = useRef("");
  useEffect(() => {
    const search = location.search || "";
    const params = new URLSearchParams(search);
    const qbo = params.get("qbo");
    const qboError = params.get("qbo_error");
    if (!qbo && !qboError) return;
    // If this exact search string was already handled (StrictMode remount
    // or duplicate render with same URL), do nothing.
    if (_handledCallbackRef.current === search) return;
    _handledCallbackRef.current = search;

    // Clean the URL immediately so a refresh doesn't re-trigger this.
    navigate("/finance", { replace: true });

    if (qboError) {
      toast.error("QuickBooks connect failed: " + qboError);
      return;
    }
    if (qbo === "connected") {
      // Verify against the server BEFORE claiming success.
      (async () => {
        try {
          const { data: st } = await api.get("/oauth/quickbooks/status");
          if (st?.connected === true) {
            toast.success("QuickBooks connected");
            // Refresh the page's status + summary now that we're actually connected.
            load(true);
          } else {
            // Callback URL said connected but the server disagrees — do NOT
            // show a success toast. Show a scoped error instead so the user
            // knows the flow didn't complete.
            toast.error("QuickBooks connection could not be verified. Please try Connect QuickBooks again.");
          }
        } catch {
          toast.error("Couldn't verify QuickBooks connection status.");
        }
      })();
    }
    // (no other qbo values are surfaced)
  }, [location.search]);

  const connectQbo = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/quickbooks/login");
      const authUrl = data?.authorization_url;
      // Guard: only navigate if we got back a valid absolute HTTPS URL to
      // Intuit. Anything else (empty, relative, wrong host) would either
      // reload our own page or fail silently — both undesired.
      if (typeof authUrl === "string" && /^https:\/\/(appcenter|oauth)\.intuit\.com\//i.test(authUrl)) {
        // Use assign() so this shows up as a navigation entry in browser
        // history and consistently triggers a full-page redirect.
        window.location.assign(authUrl);
        // Do NOT reset `connecting` — the page is about to unload.
      } else {
        toast.error("QuickBooks login URL missing or invalid — please try again.");
        setConnecting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start QuickBooks login");
      setConnecting(false);
    }
  };

  const disconnectQbo = async () => {
    if (!window.confirm("Disconnect QuickBooks? Finance data will show as unconnected until you reconnect.")) return;
    try {
      await api.post("/oauth/quickbooks/disconnect");
      toast.success("QuickBooks disconnected");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="finance-loading">
        <div className="h-12 w-72 rounded-md bg-brand-primary/5" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-64 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" />
          ))}
        </div>
      </div>
    );
  }

  // The authoritative "is QuickBooks connected" signal is the status
  // endpoint, NOT the summary payload. If the summary request fails
  // (e.g. QBO returned a 4xx for the Class-based P&L), we still want the
  // UI to say "connected — data fetch failed" rather than "not
  // connected" — the OAuth tokens are still valid.
  const connected = !!status?.connected;
  const ministries = data?.ministries || [];
  const env = data?.env || status?.env || "sandbox";
  const asOf = data?.ranges?.as_of;
  const classTrackingAvailable = data?.class_tracking_available;

  return (
    <div className="space-y-6" data-testid="finance-ministry-tab">
      {/* Header row */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Ministry Finance</p>
          <h2 className="font-heading text-3xl font-semibold text-brand-primary mt-1">
            QuickBooks read-only snapshot
          </h2>
          <p className="text-sm text-brand-primary/60 mt-1">
            {connected
              ? <>MTD &amp; YTD Income / Expense per ministry, plus Annual Budget usage. <span className="inline-block px-2 py-0.5 ml-1 rounded-full bg-sage/15 text-sage text-[10px] uppercase tracking-wider">{env}</span></>
              : "Connect QuickBooks Online to see live financial data for each ministry."}
            {asOf && <span className="ml-2 text-brand-primary/40">· As of {asOf}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && connected && (
            <button
              onClick={disconnectQbo}
              data-testid="finance-qbo-disconnect-btn"
              className="inline-flex items-center gap-2 rounded-full border border-brand-primary/15 bg-white text-brand-primary text-sm px-4 py-2 hover:bg-brand-primary/5 transition-colors"
            >
              <Link2 className="h-4 w-4" /> Disconnect QuickBooks
            </button>
          )}
          {/* Refresh is only meaningful when connected — hide when not.
              This also enforces zero /finance/summary calls from user
              action while QBO is disconnected. */}
          {connected && (
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="finance-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-brand-primary/15 bg-white text-brand-primary text-sm px-4 py-2 hover:bg-brand-primary/5 transition-colors disabled:opacity-50"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Connected, but Finance summary fetch failed. Show a scoped error
          so the user knows the connection is intact — no toast spam, no
          auto-retry. */}
      {connected && summaryError && (
        <Card
          data-testid="finance-summary-error-banner"
          className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4"
        >
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-brand-primary">Couldn&rsquo;t load Finance data from QuickBooks</p>
            <p className="text-sm text-brand-primary/70 mt-1" data-testid="finance-summary-error-detail">
              {summaryError.detail}
              {summaryError.status ? <span className="text-brand-primary/40"> (HTTP {summaryError.status})</span> : null}
            </p>
            <p className="text-xs text-brand-primary/50 mt-2">
              Your QuickBooks connection is still authorized. Click Refresh to try again.
            </p>
          </div>
        </Card>
      )}

      {/* Not connected banner */}
      {!connected && (
        <Card
          data-testid="finance-qbo-not-connected-banner"
          className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4"
        >
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">
              {status?.env_mismatch
                ? "QuickBooks reconnection required"
                : "QuickBooks is not connected"}
            </p>
            <p className="text-sm text-brand-primary/70 mt-1" data-testid="finance-qbo-disconnected-reason">
              {status?.env_mismatch ? (
                <>
                  The stored QuickBooks connection is for the{" "}
                  <b>{status?.stored_env || "previous"}</b> environment, but this server is now running{" "}
                  <b>{status?.env || "production"}</b>. Reconnect to authorize the correct company.
                </>
              ) : isAdmin ? (
                <>Click <b>Connect QuickBooks</b> to authorize read-only access. Finance data will appear here once the OAuth flow completes.</>
              ) : (
                <>Ask an admin to connect QuickBooks from this page.</>
              )}
            </p>
            {isAdmin && (
              <button
                onClick={connectQbo}
                disabled={connecting}
                data-testid="finance-qbo-connect-btn"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-terracotta text-white text-sm px-5 py-2.5 hover:bg-terracotta/90 transition-colors disabled:opacity-50"
              >
                <PlugZap className="h-4 w-4" />
                {connecting ? "Redirecting…" : (status?.env_mismatch ? "Reconnect QuickBooks" : "Connect QuickBooks")}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Overall totals — company-wide from QBO P&L, NOT summed from
          mapped ministries. Reflects the entire QuickBooks company.
          Only rendered when we actually have a summary payload. */}
      {connected && data && <OverallTotalsBar companyTotals={data?.company_totals} />}

      {/* Class tracking disabled banner — QBO connection is intact but
          the company file has Class tracking turned off, so per-class
          rollups are unavailable. Company totals are still accurate. */}
      {connected && data && classTrackingAvailable === false && (
        <Card
          data-testid="finance-class-tracking-off-banner"
          className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4"
        >
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-brand-primary">Class tracking is disabled in QuickBooks</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              Per-ministry breakdowns require QuickBooks Class tracking. Company-wide totals above are correct.
              {isAdmin && (
                <> To enable, in QuickBooks Online go to <b>Settings → Account and settings → Advanced → Categories</b> and turn on <b>Track classes</b>.</>
              )}
            </p>
          </div>
        </Card>
      )}

      {/* Admin-only Class → Ministry mapping editor */}
      {connected && isAdmin && (
        <ClassMappingEditor onSaved={() => load(true)} unmappedKeys={data?.unmapped || []} />
      )}

      {/* Unmapped notice (non-admins) */}
      {connected && !isAdmin && (data?.unmapped || []).length > 0 && (
        <div
          data-testid="finance-unmapped-hint-user"
          className="rounded-xl bg-brand-primary/5 border border-brand-primary/10 p-3 text-xs text-brand-primary/60"
        >
          {(data.unmapped || []).length} ministries are not yet mapped to a QuickBooks class and are hidden from this view. Ask an admin to configure them.
        </div>
      )}

      {/* Per-ministry cards (only mapped ministries appear) */}
      {ministries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ministries.map((m) => (
            <MinistryCard key={m.key} ministry={m} />
          ))}
        </div>
      )}
      {connected && data && !summaryError && ministries.length === 0 && classTrackingAvailable !== false && (
        <Card
          data-testid="finance-no-mapped-ministries"
          className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 text-sm text-brand-primary/70"
        >
          No ministries are mapped to a QuickBooks class yet.
          {isAdmin
            ? " Use the mapping editor above to select a QuickBooks Class for each ministry."
            : " Ask an admin to configure the Class → Ministry mapping."}
        </Card>
      )}
    </div>
  );
}


function AccountMultiSelect({ testIdPrefix, label, helper, accounts, selectedIds, onChange }) {
  const [query, setQuery] = useState("");
  const q = (query || "").trim().toLowerCase();
  const list = (accounts || [])
    .filter((a) =>
      !q ||
      (a.fq_name || "").toLowerCase().includes(q) ||
      (a.name || "").toLowerCase().includes(q) ||
      (a.id || "").toLowerCase().includes(q),
    )
    .sort((a, b) => (a.fq_name || a.name || "").localeCompare(b.fq_name || b.name || ""));
  const selectedSet = new Set(selectedIds || []);
  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div data-testid={`${testIdPrefix}-wrapper`} className="rounded-xl bg-white border border-brand-primary/10 p-3">
      <p className="text-xs font-semibold text-brand-primary">{label}</p>
      <p className="text-[11px] text-brand-primary/60 mt-0.5">{helper}</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search accounts by name…"
          data-testid={`${testIdPrefix}-search`}
          className="flex-1 rounded-lg border border-brand-primary/15 bg-white text-brand-primary text-xs px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-terracotta/30"
        />
        <span className="text-[11px] text-brand-primary/60 whitespace-nowrap" data-testid={`${testIdPrefix}-count`}>
          {selectedSet.size} selected
        </span>
      </div>
      <div
        data-testid={`${testIdPrefix}-list`}
        className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-brand-primary/10 divide-y divide-brand-primary/5"
      >
        {list.length === 0 && (
          <p className="p-3 text-xs text-brand-primary/50">No matching accounts.</p>
        )}
        {list.map((a) => (
          <label
            key={a.id}
            data-testid={`${testIdPrefix}-item-${a.id}`}
            className="flex items-start gap-2 px-3 py-2 hover:bg-brand-primary/5 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedSet.has(a.id)}
              onChange={() => toggle(a.id)}
              className="mt-0.5 accent-terracotta"
              data-testid={`${testIdPrefix}-checkbox-${a.id}`}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-brand-primary truncate">
                {a.fq_name || a.name}
                {a.sub_account && <span className="ml-1 text-[10px] text-brand-primary/50">(sub)</span>}
              </span>
              <span className="block text-[10px] text-brand-primary/50">
                id {a.id} · {a.type || a.classification}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}


function ClassMappingEditor({ onSaved, unmappedKeys }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [classes, setClasses] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [openAccountsFor, setOpenAccountsFor] = useState(null);

  const load = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const [m, cl, ac] = await Promise.all([
        api.get("/finance/mapping").then((r) => r.data),
        api.get("/finance/classes").then((r) => r.data),
        api.get("/finance/accounts").then((r) => r.data),
      ]);
      setRows(m.ministries || []);
      setClasses(cl.classes || []);
      setAccounts(ac.accounts || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // The classes/accounts endpoints return a structured detail
      // object with the raw QBO response body — render the message +
      // body so admins can see exactly what QuickBooks rejected
      // without needing server logs. Fall back to a string detail for
      // other endpoints.
      if (detail && typeof detail === "object") {
        const parts = [detail.message || "QuickBooks fetch failed."];
        if (detail.qbo_status) parts.push(`QBO status: ${detail.qbo_status}`);
        if (detail.qbo_body) parts.push(`QBO says: ${detail.qbo_body}`);
        setError(parts.join("\n"));
      } else {
        setError(detail || "Failed to load mapping data");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!open && !rows) load();
    setOpen(!open);
  };

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    if (saving || !rows) return;
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        key: r.key,
        class_id: r.class_id || "",
        class_name: r.class_name || (classes || []).find((c) => c.id === r.class_id)?.name || "",
        annual_budget_override:
          r.annual_budget_override === "" || r.annual_budget_override == null
            ? null
            : Number(r.annual_budget_override),
        income_account_ids: r.income_account_ids || [],
        expense_account_ids: r.expense_account_ids || [],
      }));
      await api.put("/finance/mapping", { ministries: payload });
      toast.success("Mapping saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  const unmappedCount = (unmappedKeys || []).length;

  return (
    <Card
      data-testid="finance-mapping-editor"
      className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5"
    >
      <button
        type="button"
        data-testid="finance-mapping-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-terracotta font-semibold">Admin only</p>
          <p className="font-heading text-base text-brand-primary mt-0.5">
            QuickBooks Class → Ministry mapping
          </p>
          <p className="text-xs text-brand-primary/60 mt-0.5">
            {unmappedCount > 0
              ? `${unmappedCount} of 10 ministries not yet mapped — click to configure.`
              : "All ministries mapped. Click to review or adjust."}
          </p>
        </div>
        <ArrowRight className={`h-4 w-4 text-brand-primary/60 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="mt-5 space-y-3">
          {loading && (
            <p className="text-sm text-brand-primary/60" data-testid="finance-mapping-loading">Loading QuickBooks classes…</p>
          )}
          {error && (
            <p className="text-sm text-terracotta whitespace-pre-wrap break-words" data-testid="finance-mapping-error">{error}</p>
          )}
          {!loading && rows && classes && (
            <>
              <div className="text-xs text-brand-primary/60 mb-2">
                For each ministry, pick the QuickBooks Class that identifies its transactions.
                Leave the class blank to hide a ministry from the dashboard.
                Use <strong>Accounts</strong> to explicitly attach QBO income / expense accounts to a ministry
                (needed for ministries that aren&apos;t their own QBO class — e.g., FCS, FC Kids inside the Church class).
                Enter an <strong>Annual Budget Override</strong> only if the ministry has no class-level budget in QuickBooks.
              </div>
              <div className="rounded-xl border border-brand-primary/10 overflow-hidden">
                <table className="w-full text-sm" data-testid="finance-mapping-table">
                  <thead>
                    <tr className="bg-brand-primary/5 text-brand-primary/70 text-[11px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Ministry</th>
                      <th className="text-left px-3 py-2">QuickBooks Class</th>
                      <th className="text-left px-3 py-2">Accounts</th>
                      <th className="text-right px-3 py-2">Annual Budget Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isExpanded = openAccountsFor === r.key;
                      const incCount = (r.income_account_ids || []).length;
                      const expCount = (r.expense_account_ids || []).length;
                      return (
                    <React.Fragment key={r.key}>
                      <tr className="border-t border-brand-primary/10" data-testid={`finance-mapping-row-${r.key}`}>
                        <td className="px-3 py-2 text-brand-primary font-medium">{r.label}</td>
                        <td className="px-3 py-2">
                          <select
                            data-testid={`finance-mapping-class-${r.key}`}
                            value={r.class_id || ""}
                            onChange={(e) => {
                              const cid = e.target.value;
                              const cname = classes.find((c) => c.id === cid)?.name || "";
                              updateRow(r.key, { class_id: cid, class_name: cname });
                            }}
                            className="w-full rounded-lg border border-brand-primary/15 bg-white text-brand-primary text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-terracotta/30"
                          >
                            <option value="">— unmapped —</option>
                            {classes.map((c) => (
                              <option key={c.id} value={c.id}>{c.fq_name || c.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            data-testid={`finance-mapping-accounts-toggle-${r.key}`}
                            onClick={() => setOpenAccountsFor(isExpanded ? null : r.key)}
                            className="w-full text-left rounded-lg border border-brand-primary/15 bg-white text-brand-primary text-xs px-2 py-1.5 hover:bg-brand-primary/5 flex items-center justify-between gap-2"
                          >
                            <span>
                              {incCount + expCount === 0
                                ? "None (class mode)"
                                : `${incCount} inc · ${expCount} exp`}
                            </span>
                            <ArrowRight className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} strokeWidth={2} />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="1"
                            placeholder="—"
                            value={r.annual_budget_override ?? ""}
                            onChange={(e) => updateRow(r.key, { annual_budget_override: e.target.value })}
                            data-testid={`finance-mapping-budget-${r.key}`}
                            className="w-32 ml-auto block text-right rounded-lg border border-brand-primary/15 bg-white text-brand-primary text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-terracotta/30"
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-brand-primary/[0.03] border-t border-brand-primary/10">
                          <td colSpan={4} className="px-3 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <AccountMultiSelect
                                testIdPrefix={`finance-mapping-inc-${r.key}`}
                                label="Income accounts"
                                helper="Sum these accounts' income for this ministry (within its class)."
                                accounts={(accounts || []).filter((a) => (a.classification || "").toLowerCase() === "revenue")}
                                selectedIds={r.income_account_ids || []}
                                onChange={(ids) => updateRow(r.key, { income_account_ids: ids })}
                              />
                              <AccountMultiSelect
                                testIdPrefix={`finance-mapping-exp-${r.key}`}
                                label="Expense accounts"
                                helper="Sum these accounts' expenses for this ministry (within its class)."
                                accounts={(accounts || []).filter((a) => (a.classification || "").toLowerCase() === "expense")}
                                selectedIds={r.expense_account_ids || []}
                                onChange={(ids) => updateRow(r.key, { expense_account_ids: ids })}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-brand-primary/15 bg-white text-brand-primary text-sm px-4 py-2 hover:bg-brand-primary/5 transition-colors"
                  data-testid="finance-mapping-cancel-btn"
                >
                  Close
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="finance-mapping-save-btn"
                  className="rounded-full bg-terracotta text-white text-sm px-5 py-2 hover:bg-terracotta/90 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save mapping"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Finance() {
  // Tab state driven by URL hash so links like /finance#giving deep-link.
  const location = useLocation();
  const initialTab = location.hash === "#giving" ? "giving" : "ministry";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    setTab(location.hash === "#giving" ? "giving" : "ministry");
  }, [location.hash]);
  const navigate = useNavigate();
  const setTabAndHash = (t) => {
    setTab(t);
    navigate(t === "giving" ? "/finance#giving" : "/finance", { replace: true });
  };

  return (
    <div className="space-y-8" data-testid="finance-page">
      <div className="flex items-center gap-2">
        <Landmark className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
        <div
          data-testid="finance-tabs"
          className="inline-flex rounded-full bg-white border border-brand-primary/10 shadow-warm p-1"
        >
          <TabButton
            active={tab === "ministry"}
            onClick={() => setTabAndHash("ministry")}
            testid="finance-tab-ministry"
          >
            Ministry Finance
          </TabButton>
          <TabButton
            active={tab === "giving"}
            onClick={() => setTabAndHash("giving")}
            testid="finance-tab-giving"
          >
            Giving
          </TabButton>
        </div>
      </div>

      {tab === "ministry" ? <MinistryFinanceTab /> : <Giving />}
    </div>
  );
}
